// Stale-while-revalidate cache for the tab screens (Stats / Results / Groups /
// Profile). Keyed by `${screen}:${uid}`. Two layers:
//   1. An in-memory Map at module scope — survives React Router's unmount/remount
//      on every tab switch, so navigating away and back is instant (no Loading
//      wall, no refetch round-trip on the hot path).
//   2. A localStorage mirror — survives a FULL reload / PWA cold start / tab
//      restore, where the Map is gone. Without it, every cold entry paints a
//      blank "Loading…" wall behind the screen's network fetch (the slow Groups
//      tab complaint). With it, the screen paints last-known content instantly
//      and still refetches in the background.
//
// Keyed by uid, so login / delete → new-anon never reads another account's data.
// Each persisted blob is stamped with the PLAY DAY and only rehydrated when it
// still matches — so a blob from a previous day never paints yesterday's "Today"
// daily points for a beat. The play day rolls at 3am ET (= midnight PT), NOT ET
// midnight (see the day-boundary rule); shifting back 3h then taking the ET date
// mirrors that roll. All localStorage access is try/caught so private-mode /
// quota failures degrade to the in-memory-only behavior, never throw.
import { playDayString } from './etDate.js';

const _cache = new Map();
const LS_PREFIX = 'ps_screen:';

// Single source of truth for the 3am-ET play-day offset (etDate.js); don't
// re-inline it here, or a future PLAY_DAY_OFFSET_HOURS change would silently
// not propagate and the cache rollover would drift from the daily-set key.
const playDayKey = playDayString;

// JSON with Date preservation. The replacer reads `this[key]` (the pre-toJSON
// value) because Date.prototype.toJSON has already stringified a Date by the
// time the replacer sees `value`. fetchUserProfile's `createdAt` is the only
// Date in the cached payloads today; this keeps any future ones safe too.
function serialize(value) {
  return JSON.stringify(value, function reviveDates(key, val) {
    const original = this[key];
    if (original instanceof Date) return { __t: 'date', v: original.toISOString() };
    return val;
  });
}
function deserialize(raw) {
  return JSON.parse(raw, (key, val) => {
    if (val && typeof val === 'object' && val.__t === 'date') return new Date(val.v);
    return val;
  });
}

export function getScreenCache(key) {
  if (!key) return null;
  if (_cache.has(key)) return _cache.get(key);
  // Cold path (Map empty after a full reload): rehydrate from localStorage if a
  // same-play-day blob exists, so the screen paints instantly instead of the
  // Loading wall. A stale-day blob is dropped rather than painted.
  try {
    const raw = localStorage.getItem(LS_PREFIX + key);
    if (raw) {
      const parsed = deserialize(raw);
      if (parsed && parsed.day === playDayKey() && 'value' in parsed) {
        _cache.set(key, parsed.value);
        return parsed.value;
      }
      localStorage.removeItem(LS_PREFIX + key);
    }
  } catch {
    /* private mode / quota / parse error — fall through to a fresh load */
  }
  return null;
}

export function setScreenCache(key, value) {
  if (!key) return;
  _cache.set(key, value);
  try {
    localStorage.setItem(LS_PREFIX + key, serialize({ day: playDayKey(), value }));
  } catch {
    /* private mode / quota — the in-memory cache still serves this session */
  }
}
